/**
 * DangoteReports — Corporate PDF Report Suite
 * Route: /corporate/reports
 *
 * 5 institutional-grade PDF reports generated from approved monthly dataset.
 * Design: McKinsey/Bain standard. No recalculation in PDF layer.
 */
import React, { useState, useMemo } from "react";
import { FileText, Download, Shield, Activity, DollarSign, Leaf, BarChart2, TrendingDown } from "lucide-react";
import { PageHeader, SectionCard } from "@/components/ds";
import { useDangote } from "@/context/DangoteContext";
import client from "@/api/client";

const REPORTS = [
  {
    key: "corporate",
    label: "Corporate Climate & Circularity Report",
    badge: "Primary",
    badgeClass: "bg-teal-100 text-teal-700 border-teal-200",
    icon: Activity,
    desc: "Consolidated Scope 1/2/3 by BU. Circularity metrics. SDG contributions. Carbon value summary. Top 5 opportunities. Methodology + traceability.",
    pages: "8 pages",
    endpoint: (pid) => `/dangote/pdf/corporate/${pid}`,
    filename: (pid) => `dangote-corporate-${pid}.pdf`,
  },
  {
    key: "bu",
    label: "Business Unit Report",
    badge: "BU-Level",
    badgeClass: "bg-blue-100 text-blue-700 border-blue-200",
    icon: BarChart2,
    desc: "Asset-level detail per BU. Scope breakdown, circularity KPIs, identified opportunities. Select BU before downloading.",
    pages: "3 pages per BU",
    endpoint: (pid, buId) => `/dangote/pdf/bu/${buId}/${pid}`,
    filename: (pid, buId) => `dangote-bu-${buId}-${pid}.pdf`,
    needsBu: true,
  },
  {
    key: "value-strategy",
    label: "Carbon Value & Strategy Report",
    badge: "Carbon",
    badgeClass: "bg-emerald-100 text-emerald-700 border-emerald-200",
    icon: TrendingDown,
    desc: "Value Bridge summary. Opportunity ranking by composite score. Top 5 vs Top 10 scenario comparison. Strategy selection framework.",
    pages: "4 pages",
    endpoint: (pid) => `/dangote/pdf/value-strategy/${pid}`,
    filename: (pid) => `dangote-value-strategy-${pid}.pdf`,
  },
  {
    key: "financing",
    label: "Financing Report",
    badge: "Finance",
    badgeClass: "bg-amber-100 text-amber-700 border-amber-200",
    icon: DollarSign,
    desc: "CAPEX summary. Annual savings (A. Bankable). Carbon value separate (B. Conditional). Payback/IRR. Bankability assessment per opportunity.",
    pages: "3 pages",
    endpoint: (pid) => `/dangote/pdf/financing/${pid}`,
    filename: (pid) => `dangote-financing-${pid}.pdf`,
  },
  {
    key: "sdg",
    label: "SDG Contribution Report",
    badge: "SDG",
    badgeClass: "bg-purple-100 text-purple-700 border-purple-200",
    icon: Leaf,
    desc: "SDG 7, 9, 11, 12, 13 mapping. Contribution type (Direct/Indirect/Conditional). Source metrics. Confidence level. Evidence linkage summary.",
    pages: "2 pages",
    endpoint: (pid) => `/dangote/pdf/sdg/${pid}`,
    filename: (pid) => `dangote-sdg-${pid}.pdf`,
  },
];

const BU_LIST = [
  { id: "BU-001", name: "Dangote Cement" },
  { id: "BU-002", name: "Dangote Refinery" },
  { id: "BU-003", name: "Dangote Fertilizer" },
  { id: "BU-004", name: "Dangote Sugar" },
  { id: "BU-005", name: "Dangote Logistics" },
];

export default function DangoteReports() {
  const { selectedPeriod, period, periods, setSelectedPeriod } = useDangote();
  const [loading, setLoading] = useState({});
  const [errors, setErrors] = useState({});
  const [selectedBu, setSelectedBu] = useState("BU-001");

  // Convert period_id → "Jan2026" label for filenames
  const periodLabel = useMemo(() => {
    const p = periods.find(x => x.period_id === selectedPeriod);
    return p ? p.label.replace(" ", "") : (selectedPeriod || "");
  }, [periods, selectedPeriod]);

  const handleDownload = async (report) => {
    const key = report.key;
    setLoading(prev => ({ ...prev, [key]: true }));
    setErrors(prev => ({ ...prev, [key]: null }));
    try {
      const endpoint = report.needsBu
        ? report.endpoint(selectedPeriod, selectedBu)
        : report.endpoint(selectedPeriod);

      // Filename: Dangote_[ReportType]_[Jan2026].pdf
      const fileNames = {
        "corporate":      `Dangote_Climate_${periodLabel}.pdf`,
        "bu":             `Dangote_BU_${selectedBu}_${periodLabel}.pdf`,
        "value-strategy": `Dangote_ValueStrategy_${periodLabel}.pdf`,
        "financing":      `Dangote_Financing_${periodLabel}.pdf`,
        "sdg":            `Dangote_SDG_${periodLabel}.pdf`,
      };
      const filename = fileNames[key] || `Dangote_${key}_${periodLabel}.pdf`;

      const resp = await client.get(endpoint, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([resp.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      link.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      const msg = err?.response?.status === 404
        ? "No data for this period. Submit and calculate data first."
        : "PDF generation failed. Please try again.";
      setErrors(prev => ({ ...prev, [key]: msg }));
    } finally {
      setLoading(prev => ({ ...prev, [key]: false }));
    }
  };

  return (
    <div className="p-6 lg:p-8 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-0.5">Dangote Industries</p>
          <PageHeader title="Reports" />
          <p className="text-xs text-slate-400 mt-0.5">
            Institutional-grade · Period-based · All data from approved monthly dataset
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select value={selectedPeriod || ""} onChange={e => setSelectedPeriod(e.target.value)}
            className="text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white text-slate-700"
            data-testid="report-period-selector">
            {periods.map(p => <option key={p.period_id} value={p.period_id}>{p.label}</option>)}
          </select>
          <span className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-2.5 py-1.5">
            {period?.label}
          </span>
        </div>
      </div>

      {/* Validation banner */}
      <div className="bg-amber-50 border border-amber-200 rounded-xl px-5 py-3 flex items-start gap-2">
        <Shield size={14} className="text-amber-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-xs font-bold text-amber-700">Validation-Ready Model Outputs</p>
          <p className="text-xs text-amber-600/80">
            All reports are generated from approved monthly calculation data. Not independently verified.
            Not equivalent to certified GHG inventories or carbon credits.
            Engage an accredited VVB for independent verification before external submission.
          </p>
        </div>
      </div>

      {/* BU selector for BU report */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-center gap-3">
        <p className="text-xs font-semibold text-slate-600 whitespace-nowrap">Business Unit (for BU Report):</p>
        <select value={selectedBu} onChange={e => setSelectedBu(e.target.value)}
          className="text-xs border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white text-slate-700"
          data-testid="bu-selector">
          {BU_LIST.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
        </select>
        <p className="text-[10px] text-slate-400">Applies only to the BU Report</p>
      </div>

      {/* Report cards */}
      <div className="space-y-4" data-testid="reports-list">
        {REPORTS.map(r => (
          <div key={r.key}
            className="bg-white border border-slate-200 rounded-xl p-5 hover:border-teal-200 transition-colors"
            data-testid={`report-card-${r.key}`}>
            <div className="flex items-start gap-3">
              <div className="w-9 h-9 bg-slate-50 rounded-xl flex items-center justify-center flex-shrink-0 border border-slate-100">
                <r.icon size={16} className="text-teal-600" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap mb-1">
                  <p className="text-sm font-semibold text-slate-900">{r.label}</p>
                  <span className={`text-[9px] font-bold px-2 py-0.5 rounded-full border ${r.badgeClass}`}>{r.badge}</span>
                  <span className="text-xs text-slate-400">{r.pages}</span>
                </div>
                <p className="text-xs text-slate-500 leading-relaxed">{r.desc}</p>
                {errors[r.key] && (
                  <p className="text-xs text-red-500 mt-1.5 font-medium" data-testid={`error-${r.key}`}>
                    {errors[r.key]}
                  </p>
                )}
              </div>
              <button
                onClick={() => handleDownload(r)}
                disabled={loading[r.key] || !selectedPeriod}
                className="flex items-center gap-1.5 px-3.5 py-2 bg-[#0F172A] text-white text-xs font-bold rounded-xl hover:bg-slate-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-shrink-0 whitespace-nowrap"
                data-testid={`download-${r.key}`}>
                {loading[r.key]
                  ? <><span className="animate-spin inline-block w-3 h-3 border border-white border-t-transparent rounded-full mr-1" />Generating...</>
                  : <><Download size={12} /> Download PDF</>
                }
              </button>
            </div>
          </div>
        ))}
      </div>

      <p className="text-xs text-slate-400 italic text-center">
        Reports are generated on demand from the approved monthly dataset. Generation time: 3–8 seconds per report.
      </p>
    </div>
  );
}
